/**
 * AdminUsersPage Component
 *
 * User management page for admins.
 * Shows full users list with search filtering and admin toggle.
 */

import { useState, useEffect, type CSSProperties } from 'react';
import { collection, query, orderBy, getDocs, type DocumentData } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseDb, getFirebaseFunctions } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import type { User } from '@flash/shared';

/**
 * Convert Firestore document to User type
 */
const docToUser = (docId: string, docData: DocumentData): User => {
  return {
    id: docId,
    googleId: (docData['googleId'] as string) ?? '',
    email: (docData['email'] as string) ?? '',
    displayName: (docData['displayName'] as string) ?? '',
    photoURL: (docData['photoURL'] as string | null) ?? null,
    isAdmin: (docData['isAdmin'] as boolean) ?? false,
    isOnboarded: (docData['isOnboarded'] as boolean) ?? false,
    createdAt: docData['createdAt'],
    updatedAt: docData['updatedAt'],
  };
};

/**
 * Response type from setUserAdmin callable
 */
interface SetUserAdminResponse {
  success: boolean;
}

/**
 * Format a Firestore timestamp for display
 */
const formatTimestamp = (timestamp: unknown): string => {
  if (!timestamp) return 'N/A';
  if (typeof timestamp === 'object' && timestamp !== null && 'toDate' in timestamp) {
    return (timestamp as { toDate: () => Date }).toDate().toLocaleString();
  }
  if (timestamp instanceof Date) {
    return timestamp.toLocaleString();
  }
  return 'N/A';
};

/**
 * AdminUsersPage displays full users list with search and admin management
 */
export const AdminUsersPage = () => {
  const { user: currentUser } = useAuth();

  // Users data
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Toggle admin state
  const [togglingUserId, setTogglingUserId] = useState<string | null>(null);
  const [confirmUserId, setConfirmUserId] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  // Fetch all users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const db = getFirebaseDb();
        const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(usersQuery);
        const usersData = snapshot.docs.map((doc) => docToUser(doc.id, doc.data()));
        setUsers(usersData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  // Clear toggle error after 5 seconds
  useEffect(() => {
    if (toggleError) {
      const timer = setTimeout(() => setToggleError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toggleError]);

  // Filter users by search term
  const filteredUsers = users.filter(
    (user) =>
      user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /**
   * Handle admin toggle with confirmation
   */
  const handleToggleAdmin = async (targetUser: User) => {
    if (confirmUserId === targetUser.id) {
      setTogglingUserId(targetUser.id);
      setToggleError(null);

      try {
        const functions = getFirebaseFunctions();
        const setUserAdmin = httpsCallable<
          { userId: string; isAdmin: boolean },
          SetUserAdminResponse
        >(functions, 'setUserAdmin');

        await setUserAdmin({
          userId: targetUser.id,
          isAdmin: !targetUser.isAdmin,
        });

        setUsers((prev) =>
          prev.map((u) => (u.id === targetUser.id ? { ...u, isAdmin: !targetUser.isAdmin } : u))
        );
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update admin status';
        console.error('Error toggling admin status:', err);
        setToggleError(errorMessage);
      } finally {
        setTogglingUserId(null);
        setConfirmUserId(null);
      }
    } else {
      setConfirmUserId(targetUser.id);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmUserId(null);
  };

  const isCurrentUser = (userId: string) => userId === currentUser?.uid;

  if (loading) {
    return (
      <main style={containerStyle}>
        <div style={loadingContainerStyle}>
          <p style={loadingTextStyle}>Loading users...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main style={containerStyle}>
        <div style={contentStyle}>
          <div style={errorContainerStyle}>
            <h2 style={errorTitleStyle}>Error Loading Users</h2>
            <p style={errorTextStyle}>{error}</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <header style={headerStyle}>
          <h1 style={titleStyle}>Manage Users</h1>
          <p style={subtitleStyle}>View all users and manage admin privileges</p>
        </header>

        {/* Search */}
        <div style={searchContainerStyle}>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />
        </div>

        {/* Toggle Error */}
        {toggleError && (
          <div style={errorBannerStyle}>
            <p style={errorBannerTextStyle}>{toggleError}</p>
          </div>
        )}

        {/* Users Table */}
        <div style={tableContainerStyle}>
          <table style={tableStyle}>
            <thead>
              <tr style={tableHeaderRowStyle}>
                <th style={tableHeaderCellStyle}>User</th>
                <th style={tableHeaderCellStyle}>Email</th>
                <th style={tableHeaderCellStyle}>Status</th>
                <th style={tableHeaderCellStyle}>Created</th>
                <th style={tableHeaderCellStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} style={emptyRowStyle}>
                    {searchTerm ? 'No users match your search' : 'No users found'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((targetUser) => {
                  const isToggling = togglingUserId === targetUser.id;
                  const isConfirming = confirmUserId === targetUser.id;
                  const isSelf = isCurrentUser(targetUser.id);

                  return (
                    <tr key={targetUser.id} style={tableRowStyle}>
                      {/* User */}
                      <td style={tableCellStyle}>
                        <div style={userCellStyle}>
                          {targetUser.photoURL ? (
                            <img src={targetUser.photoURL} alt="" style={avatarStyle} />
                          ) : (
                            <div style={avatarPlaceholderStyle}>
                              {(targetUser.displayName || targetUser.email || 'U')
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                          )}
                          <span style={userNameStyle}>{targetUser.displayName || 'No name'}</span>
                        </div>
                      </td>

                      {/* Email */}
                      <td style={tableCellStyle}>
                        <span style={emailStyle}>{targetUser.email}</span>
                      </td>

                      {/* Status */}
                      <td style={tableCellStyle}>
                        <div style={statusCellStyle}>
                          {targetUser.isAdmin && <span style={adminBadgeStyle}>Admin</span>}
                          {isSelf && <span style={selfBadgeStyle}>You</span>}
                          {!targetUser.isAdmin && !isSelf && (
                            <span style={userBadgeStyle}>User</span>
                          )}
                        </div>
                      </td>

                      {/* Created */}
                      <td style={tableCellStyle}>
                        <span style={dateStyle}>{formatTimestamp(targetUser.createdAt)}</span>
                      </td>

                      {/* Actions */}
                      <td style={tableCellStyle}>
                        {isConfirming && !isToggling ? (
                          <div style={confirmContainerStyle}>
                            <span style={confirmTextStyle}>
                              {targetUser.isAdmin ? 'Remove admin?' : 'Make admin?'}
                            </span>
                            <div style={confirmButtonsStyle}>
                              <button
                                onClick={() => handleToggleAdmin(targetUser)}
                                style={confirmYesButtonStyle}
                                disabled={isSelf && targetUser.isAdmin}
                              >
                                Yes
                              </button>
                              <button onClick={handleCancelConfirm} style={confirmNoButtonStyle}>
                                No
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleToggleAdmin(targetUser)}
                            disabled={isToggling || (isSelf && targetUser.isAdmin)}
                            style={{
                              ...toggleButtonStyle,
                              ...(targetUser.isAdmin
                                ? removeAdminButtonStyle
                                : grantAdminButtonStyle),
                              ...(isToggling || (isSelf && targetUser.isAdmin)
                                ? disabledButtonStyle
                                : {}),
                            }}
                            title={
                              isSelf && targetUser.isAdmin
                                ? 'You cannot remove your own admin status'
                                : undefined
                            }
                          >
                            {isToggling
                              ? 'Updating...'
                              : targetUser.isAdmin
                                ? 'Remove Admin'
                                : 'Make Admin'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Stats */}
        <p style={statsStyle}>
          Showing {filteredUsers.length} of {users.length} users
        </p>
      </div>
    </main>
  );
};

/**
 * Styles
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  backgroundColor: 'var(--color-bg-secondary)',
};

const contentStyle: CSSProperties = {
  maxWidth: '1000px',
  margin: '0 auto',
  padding: '20px',
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '1.125rem',
  color: 'var(--color-text-secondary)',
};

const errorContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px 20px',
  textAlign: 'center',
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
};

const errorTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginBottom: '8px',
};

const errorTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '16px',
};

const headerStyle: CSSProperties = {
  marginBottom: '24px',
};

const titleStyle: CSSProperties = {
  fontSize: '1.75rem',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: '12px 0 4px 0',
};

const subtitleStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

const searchContainerStyle: CSSProperties = {
  marginBottom: '20px',
};

const searchInputStyle: CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  fontSize: '1rem',
  boxSizing: 'border-box',
};

const errorBannerStyle: CSSProperties = {
  padding: '12px 16px',
  backgroundColor: '#fee2e2',
  border: '1px solid #fecaca',
  borderRadius: '8px',
  marginBottom: '16px',
};

const errorBannerTextStyle: CSSProperties = {
  color: '#991b1b',
  fontSize: '0.875rem',
  margin: 0,
};

const tableContainerStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
  overflow: 'hidden',
};

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

const tableHeaderRowStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-secondary)',
  borderBottom: '1px solid var(--color-border)',
};

const tableHeaderCellStyle: CSSProperties = {
  textAlign: 'left',
  padding: '12px 16px',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const tableRowStyle: CSSProperties = {
  borderBottom: '1px solid var(--color-border)',
};

const tableCellStyle: CSSProperties = {
  padding: '12px 16px',
  verticalAlign: 'middle',
};

const emptyRowStyle: CSSProperties = {
  padding: '40px 16px',
  textAlign: 'center',
  color: 'var(--color-text-muted)',
};

const userCellStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
};

const avatarStyle: CSSProperties = {
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  objectFit: 'cover',
};

const avatarPlaceholderStyle: CSSProperties = {
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  backgroundColor: 'var(--color-bg-tertiary)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
};

const userNameStyle: CSSProperties = {
  fontWeight: 500,
  color: 'var(--color-text-primary)',
};

const emailStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
};

const statusCellStyle: CSSProperties = {
  display: 'flex',
  gap: '6px',
  flexWrap: 'wrap',
};

const adminBadgeStyle: CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: '#dbeafe',
  color: '#1d4ed8',
  borderRadius: '9999px',
};

const selfBadgeStyle: CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: 'var(--color-bg-secondary)',
  color: 'var(--color-text-secondary)',
  borderRadius: '9999px',
};

const userBadgeStyle: CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: 'var(--color-bg-secondary)',
  color: 'var(--color-text-muted)',
  borderRadius: '9999px',
};

const dateStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
};

const toggleButtonStyle: CSSProperties = {
  padding: '6px 12px',
  border: 'none',
  borderRadius: '6px',
  fontSize: '0.75rem',
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const grantAdminButtonStyle: CSSProperties = {
  backgroundColor: '#dbeafe',
  color: '#1d4ed8',
};

const removeAdminButtonStyle: CSSProperties = {
  backgroundColor: '#fee2e2',
  color: '#991b1b',
};

const disabledButtonStyle: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
};

const confirmContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
};

const confirmTextStyle: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
};

const confirmButtonsStyle: CSSProperties = {
  display: 'flex',
  gap: '6px',
};

const confirmYesButtonStyle: CSSProperties = {
  padding: '4px 10px',
  backgroundColor: '#3b82f6',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  fontSize: '0.75rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const confirmNoButtonStyle: CSSProperties = {
  padding: '4px 10px',
  backgroundColor: 'var(--color-bg-secondary)',
  color: 'var(--color-text-secondary)',
  border: 'none',
  borderRadius: '4px',
  fontSize: '0.75rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const statsStyle: CSSProperties = {
  marginTop: '16px',
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  textAlign: 'center',
};

export default AdminUsersPage;
